import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f4f8;
  padding: 2rem;
`;

const FormContainer = styled(motion.div)`
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(28, 47, 79, 0.1);
  padding: 3rem;
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h2`
  color: #1c2f4f;
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 2rem;
`;

const Note = styled.p`
  font-size: 0.9rem;
  color: #5a7184;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled(motion.form)`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  border: 2px solid #e6eef8;
  border-radius: 50px;
  background-color: #f8fafc;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1c2f4f;
    background-color: white;
  }
`;

const Label = styled.label`
  position: absolute;
  left: 1rem;
  top: 1rem;
  padding: 0 0.5rem;
  color: #5a7184;
  transition: all 0.3s ease;
  pointer-events: none;

  ${Input}:focus + &,
  ${Input}:not(:placeholder-shown) + & {
    top: -0.5rem;
    left: 0.8rem;
    font-size: 0.8rem;
    background-color: white;
  }
`;

const Button = styled(motion.button)`
  padding: 1rem;
  font-size: 1rem;
  border: none;
  border-radius: 50px;
  background-color: #1c2f4f;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    background-color: #2c3e50;
  }
`;

const ErrorMessage = styled(motion.p)`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 1rem;
  text-align: center;
`;

const SuccessMessage = styled(motion.p)`
  color: #2ecc71;
  font-size: 0.9rem;
  margin-top: 1rem;
  text-align: center;
`;

const TutorLogin = ({ setToken }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      console.log('Sending login request with:', { username, password });
      const response = await axios.post('/auth/login', { username, password });
      console.log('Login response:', response.data);
      setToken(response.data.token);
      localStorage.setItem('token', response.data.token);
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/update-availability');
      }, 1500);
    } catch (error) {
      console.error('Detailed error information:');
      console.error('- Message:', error.message);
      console.error('- Name:', error.name);
      console.error('- Code:', error.code);
      if (error.response) {
        console.error('- Response:');
        console.error('  - Data:', error.response.data);
        console.error('  - Status:', error.response.status);
        console.error('  - Headers:', error.response.headers);
      } else {
        console.error('- No response received');
      }
      if (error.config) {
        console.error('- Request Config:');
        console.error('  - URL:', error.config.url);
        console.error('  - Method:', error.config.method);
        console.error('  - Headers:', error.config.headers);
        console.error('  - Data:', error.config.data);
      }
      
      let errorMessage = 'An error occurred during login. Please try again.';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response received from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <FormContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Title>Tutor Login</Title>
        <Note>For EMOO tutors only</Note>
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder=" "
            />
            <Label htmlFor="username">Username</Label>
          </InputGroup>
          <InputGroup>
            <Input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder=" "
            />
            <Label htmlFor="password">Password</Label>
          </InputGroup>
          <Button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
        </Form>
        <AnimatePresence>
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </ErrorMessage>
          )}
          {isSuccess && (
            <SuccessMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              Login successful! Redirecting...
            </SuccessMessage>
          )}
        </AnimatePresence>
      </FormContainer>
    </PageContainer>
  );
};

export default TutorLogin;